import { hasValue } from "@arti/utilities";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import type {
  FlowType,
  TaskFilterFacets,
  TaskFilters,
  TaskPage,
  TaskSnapshot,
} from "../../types";

import { getTaskFacetsAsUser, listTasksAsUser } from "../../dashboard-client";
import {
  parseListParam,
  parseNumberParam,
  serializeListParam,
  updateSearchParams,
} from "../../utils/searchParams";
import { groupTasksByFlow } from "../../utils/taskFlowGrouping";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { TasksEmptyState } from "../common/EmptyState";
import { HudPanel } from "../common/HudPanel";
import { HudStatusPill } from "../common/HudStatusPill";
import { Pagination } from "../common/Pagination";
import { GroupedTasksView } from "./GroupedTasksView";
import {
  TaskActivityFiltersPanel,
  TaskActivityFiltersToggle,
  getActiveFilterCount,
} from "./TaskActivityFilters";
import { TaskRow } from "./TaskRow";

type ViewMode = "grouped" | "flat";

const PAGE_SIZE = 50;
const VIEW_PARAM = "overview_view";
const PAGE_PARAM = "overview_page";
const STATUS_PARAM = "overview_status";
const TYPE_PARAM = "overview_type";
const FLOW_TYPE_PARAM = "overview_flow_type";
const TICKET_PARAM = "overview_ticket";
const DATE_FROM_PARAM = "overview_date_from";
const DATE_TO_PARAM = "overview_date_to";

function parseFlowType(value: string): FlowType | null {
  switch (value) {
    case "analysis":
    case "compile_recording":
    case "execution":
    case "plan_revision":
    case "source_control": {
      return value;
    }
    default: {
      return null;
    }
  }
}

function parseFlowTypeList(values: string[]): FlowType[] | undefined {
  const parsed = values.flatMap((value) => {
    const flowType = parseFlowType(value);
    return flowType ? [flowType] : [];
  });

  return parsed.length > 0 ? parsed : undefined;
}

function buildTaskFilterQuery(filters?: TaskFilters): {
  status?: string;
  taskType?: string;
  flowType?: string;
  ticketId?: string;
  dateFrom?: string;
  dateTo?: string;
} {
  return {
    dateFrom: filters?.dateFrom ?? undefined,
    dateTo: filters?.dateTo ?? undefined,
    flowType: filters?.flowType?.length
      ? filters.flowType.join(",")
      : undefined,
    status: filters?.status?.length ? filters.status.join(",") : undefined,
    taskType: filters?.taskType?.length
      ? filters.taskType.join(",")
      : undefined,
    ticketId: filters?.ticketId ?? undefined,
  };
}

function parseViewMode(value: string | null): ViewMode {
  return value === "flat" ? "flat" : "grouped";
}

function parseFilters(searchParams: URLSearchParams): TaskFilters {
  const status = parseListParam(searchParams.get(STATUS_PARAM));
  const legacyTaskType = parseListParam(searchParams.get(TYPE_PARAM));
  const flowTypeParam = parseListParam(searchParams.get(FLOW_TYPE_PARAM));
  const flowType = flowTypeParam.length > 0 ? flowTypeParam : legacyTaskType;
  const ticketId = searchParams.get(TICKET_PARAM) ?? undefined;
  const dateFrom = searchParams.get(DATE_FROM_PARAM) ?? undefined;
  const dateTo = searchParams.get(DATE_TO_PARAM) ?? undefined;

  return {
    dateFrom: dateFrom ?? undefined,
    dateTo: dateTo ?? undefined,
    flowType: parseFlowTypeList(flowType),
    status: status.length > 0 ? status : undefined,
    taskType: legacyTaskType.length > 0 ? legacyTaskType : undefined,
    ticketId: ticketId ?? undefined,
  };
}

function serializeFilters(filters: TaskFilters): Record<string, string | null> {
  return {
    [STATUS_PARAM]: serializeListParam(filters.status),
    [TYPE_PARAM]: null,
    [FLOW_TYPE_PARAM]: serializeListParam(filters.flowType),
    [TICKET_PARAM]: filters.ticketId ?? null,
    [DATE_FROM_PARAM]: filters.dateFrom ?? null,
    [DATE_TO_PARAM]: filters.dateTo ?? null,
  };
}

interface RecentTasksPanelProps {
  tasks: TaskSnapshot[];
  taskTotal?: number;
  onRefresh?: () => void;
  onNavigateToIndexing?: (repoSlug: string) => void;
}

export const RecentTasksPanel: FC<RecentTasksPanelProps> = ({
  tasks,
  taskTotal,
  onRefresh,
  onNavigateToIndexing,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const viewMode = parseViewMode(searchParams.get(VIEW_PARAM));
  const page = parseNumberParam(searchParams.get(PAGE_PARAM), 1);
  const filters = useMemo(
    () => parseFilters(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );

  const [paginatedData, setPaginatedData] = useState<TaskPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [facetCounts, setFacetCounts] = useState<TaskFilterFacets | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => updateSearchParams(prev, updates), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const hasActiveFilters = useMemo(
    () =>
      (hasValue(filters.status) && filters.status.length > 0) ||
      (hasValue(filters.flowType) && filters.flowType.length > 0) ||
      (hasValue(filters.taskType) && filters.taskType.length > 0) ||
      hasValue(filters.ticketId) ||
      hasValue(filters.dateFrom) ||
      hasValue(filters.dateTo),
    [filters],
  );
  const activeFilterCount = useMemo(
    () => getActiveFilterCount(filters),
    [filters],
  );

  // Reset to page 1 when tasks prop reference changes (new overview refresh)
  const prevTasksRef = useRef(tasks);
  useEffect(() => {
    if (prevTasksRef.current !== tasks) {
      prevTasksRef.current = tasks;
      if (page !== 1 && !hasActiveFilters) {
        setPaginatedData(null);
        updateParams({ [PAGE_PARAM]: null });
      }
    }
  }, [tasks, page, hasActiveFilters, updateParams]);

  const handleFilterChange = useCallback(
    (next: TaskFilters) => {
      updateParams({ ...serializeFilters(next), [PAGE_PARAM]: null });
      setPaginatedData(null);
    },
    [updateParams],
  );

  const statusKey = (filters.status ?? []).join(",");
  const taskTypeKey = (filters.taskType ?? []).join(",");
  const flowTypeKey = (filters.flowType ?? []).join(",");
  const ticketKey = filters.ticketId ?? "";
  const dateFromKey = filters.dateFrom ?? "";
  const dateToKey = filters.dateTo ?? "";

  const facetFilters = useMemo(() => {
    const status = statusKey ? statusKey.split(",") : undefined;
    const taskType = taskTypeKey ? taskTypeKey.split(",") : undefined;
    const flowType = flowTypeKey
      ? parseFlowTypeList(flowTypeKey.split(","))
      : undefined;
    return {
      dateFrom: dateFromKey || undefined,
      dateTo: dateToKey || undefined,
      flowType,
      status,
      taskType,
      ticketId: ticketKey || undefined,
    };
  }, [statusKey, taskTypeKey, flowTypeKey, ticketKey, dateFromKey, dateToKey]);

  // Fetch paginated/filtered data
  useEffect(() => {
    if (page === 1 && !hasActiveFilters) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    listTasksAsUser({
      query: {
        page: String(page),
        pageSize: String(PAGE_SIZE),
        ...buildTaskFilterQuery(hasActiveFilters ? filters : undefined),
      },
    })
      .then(({ data }) => {
        if (!cancelled) {
          setPaginatedData(data);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.error("Failed to fetch paginated tasks:", error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, filters, hasActiveFilters]);

  useEffect(() => {
    let cancelled = false;

    getTaskFacetsAsUser({ query: buildTaskFilterQuery(facetFilters) })
      .then(({ data }) => {
        if (!cancelled) {
          setFacetCounts(data);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.error("Failed to fetch task facets:", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facetFilters]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams({ [PAGE_PARAM]: newPage > 1 ? String(newPage) : null });
      setPaginatedData(null);
    },
    [updateParams],
  );

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      updateParams({ [VIEW_PARAM]: mode === "grouped" ? null : mode });
    },
    [updateParams],
  );

  const displayTasks = useMemo(
    () =>
      hasActiveFilters || page !== 1 ? (paginatedData?.entries ?? []) : tasks,
    [page, tasks, paginatedData, hasActiveFilters],
  );
  const total = hasActiveFilters
    ? (paginatedData?.total ?? 0)
    : (taskTotal ?? paginatedData?.total ?? tasks.length);

  const groupedEntries = useMemo(
    () => groupTasksByFlow(displayTasks),
    [displayTasks],
  );

  const { hasAnyTicket, hasAnyNode } = useMemo(() => {
    let ticket = false;
    let node = false;
    for (const t of displayTasks) {
      if (t.linked_ticket_id) {
        ticket = true;
      }
      if (t.assigned_node) {
        node = true;
      }
      if (ticket && node) {
        break;
      }
    }
    return { hasAnyNode: node, hasAnyTicket: ticket };
  }, [displayTasks]);

  return (
    <HudPanel
      title="Recent Task Activity"
      subtitle="Real-time execution monitoring"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <TaskActivityFiltersToggle
            expanded={filtersExpanded}
            activeCount={activeFilterCount}
            onToggle={() => setFiltersExpanded((prev) => !prev)}
          />
          {/* Segmented view mode toggle */}
          <div className="dark:border-arti-panel-border inline-flex overflow-hidden rounded-none border border-slate-300 shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={`h-auto items-center gap-1 rounded-none px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                viewMode === "grouped"
                  ? "bg-slate-100 text-slate-900 dark:bg-slate-900/90 dark:text-white"
                  : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-white/5"
              }`}
              onClick={() => handleViewModeChange("grouped")}
              title="Group by flow"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={`h-auto items-center gap-1 rounded-none border-l border-slate-300 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] dark:border-slate-700 ${
                viewMode === "flat"
                  ? "bg-slate-100 text-slate-900 dark:bg-slate-900/90 dark:text-white"
                  : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-white/5"
              }`}
              onClick={() => handleViewModeChange("flat")}
              title="Flat list"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </Button>
          </div>

          <HudStatusPill
            label={`Showing ${displayTasks.length} of ${total}`}
            tone="neutral"
            hideDot
          />
        </div>
      }
    >
      {filtersExpanded && (
        <div className="border-b border-slate-200 px-4 py-3 dark:border-white/10">
          <TaskActivityFiltersPanel
            filters={filters}
            onChange={handleFilterChange}
            counts={facetCounts ?? undefined}
          />
        </div>
      )}

      {displayTasks.length === 0 && !loading && (
        <TasksEmptyState className="my-6" />
      )}
      {loading && (
        <div className="flex items-center justify-center py-12 text-sm text-slate-500 dark:text-slate-400">
          Loading tasks...
        </div>
      )}
      {!loading && displayTasks.length > 0 && viewMode === "grouped" && (
        <GroupedTasksView
          entries={groupedEntries}
          onRefresh={onRefresh}
          onNavigateToIndexing={onNavigateToIndexing}
        />
      )}
      {!loading && displayTasks.length > 0 && viewMode !== "grouped" && (
        <DataTable maxHeight="600px">
          <DataTable.Header className="sticky top-0 z-10">
            <tr>
              <DataTable.HeaderCell>Task ID</DataTable.HeaderCell>
              <DataTable.HeaderCell>Status</DataTable.HeaderCell>
              <DataTable.HeaderCell className="hidden w-24 text-right md:table-cell">
                Cost
              </DataTable.HeaderCell>
              {hasAnyTicket && (
                <DataTable.HeaderCell className="hidden md:table-cell">
                  Ticket
                </DataTable.HeaderCell>
              )}
              {hasAnyNode && (
                <DataTable.HeaderCell className="hidden md:table-cell">
                  Node
                </DataTable.HeaderCell>
              )}
              <DataTable.HeaderCell className="hidden sm:table-cell">
                Updated
              </DataTable.HeaderCell>
              <DataTable.HeaderCell className="w-36 text-right">
                <span className="sr-only">Actions</span>
              </DataTable.HeaderCell>
            </tr>
          </DataTable.Header>
          <DataTable.Body>
            {displayTasks.map((task) => (
              <TaskRow
                key={task.task_id}
                task={task}
                onRefresh={onRefresh}
                hideTicketColumn={!hasAnyTicket}
                hideNodeColumn={!hasAnyNode}
              />
            ))}
          </DataTable.Body>
        </DataTable>
      )}

      {total > PAGE_SIZE && (
        <div className="px-4 pb-4">
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </HudPanel>
  );
};
